import React, { useRef, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import ChevronDownIcon from '../../../../_components/icons/ChevronDownIcon';

import classes from './ExceptionActionDropdown.module.scss';

export interface ActionOption {
  label: string;
  value: string;
}

interface ExceptionActionDropdownProps {
  options: ActionOption[];
  selectedOptions: string[];
  onSelect: (selected: string[]) => void;
  placeHolder?: string;
  height?: string;
  'data-testid'?: string;
}

const getDisplayText = (
  selectedOptions: string[],
  options: ActionOption[],
  placeHolder: string
): string => {
  if (!selectedOptions?.length) return placeHolder;
  if (selectedOptions.length === 1) {
    const option = options.find((o) => o.value === selectedOptions[0]);
    return option?.label ?? selectedOptions[0];
  }
  return `${selectedOptions.length} options selected`;
};

export const ExceptionActionDropdown = ({ options, onSelect, selectedOptions = [], placeHolder = 'Select option', height = '38px', 'data-testid': dataTestId = 'exception-action' }: ExceptionActionDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const idPrefix = React.useId();

  const displayText = getDisplayText(selectedOptions, options, placeHolder);

  const handleOptionChange = (value: string) => {
    const isSelected = selectedOptions?.includes(value);
    if (isSelected) {
      onSelect(selectedOptions.filter((v) => v !== value));
    } else {
      onSelect([...(selectedOptions ?? []), value]);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div
      className={classes.root}
      ref={containerRef}
      data-testid={dataTestId}
    >
      <div
        className={classes.trigger}
        style={{ height }}
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
          if (e.key === 'Escape') setIsOpen(false);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={classes.triggerText}>{displayText}</span>
        <ChevronDownIcon
          className={isOpen ? classes.chevronOpen : ''}
          pathClassName={classes.chevronPath}
        />
      </div>
      {isOpen && (
        <div className={classes.options} role="listbox">
          {options.map((option) => (
            <div
              key={option.value}
              className={classes.optionRow}
              role="option"
              aria-selected={selectedOptions?.includes(option.value)}
            >
              <Form className={classes.optionForm}>
                <Form.Check
                  type="checkbox"
                  id={`${idPrefix}-action-${option.value}`}
                  checked={selectedOptions?.includes(option.value)}
                  onChange={() => handleOptionChange(option.value)}
                  label={option.label}
                  data-testid="input-option-checkbox"
                />
              </Form>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
